/**
 * Cookie Preferences Settings Component
 *
 * Allows users to change their cookie consent choices in profile settings
 * Syncs with localStorage and backend
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Button,
  Alert,
  Divider,
  Link as MuiLink,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Cookie as CookieIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useCookieConsent } from './CookieConsent';

const CookiePreferencesSettings: React.FC = () => {
  const theme = useTheme();
  const {
    preferences,
    hasConsented,
    saveCustomPreferences,
    acceptAll,
    acceptEssentialOnly,
  } = useCookieConsent();

  const [localPrefs, setLocalPrefs] = useState({
    analytics: preferences.analytics,
    marketing: preferences.marketing,
    preferences: preferences.preferences,
  });

  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when hook preferences change
  useEffect(() => {
    setLocalPrefs({
      analytics: preferences.analytics,
      marketing: preferences.marketing,
      preferences: preferences.preferences,
    });
  }, [preferences]);

  // Check if there are unsaved changes
  useEffect(() => {
    const changed =
      localPrefs.analytics !== preferences.analytics ||
      localPrefs.marketing !== preferences.marketing ||
      localPrefs.preferences !== preferences.preferences;
    setHasChanges(changed);
  }, [localPrefs, preferences]);

  const handleToggle = (key: 'analytics' | 'marketing' | 'preferences') => {
    setLocalPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setSaved(false);
  };

  const handleSave = () => {
    saveCustomPreferences(localPrefs);
    setSaved(true);
    setHasChanges(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleAcceptAll = () => {
    acceptAll();
    setLocalPrefs({
      analytics: true,
      marketing: true,
      preferences: true,
    });
    setSaved(true);
    setHasChanges(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleEssentialOnly = () => {
    acceptEssentialOnly();
    setLocalPrefs({
      analytics: false,
      marketing: false,
      preferences: true,
    });
    setSaved(true);
    setHasChanges(false);
    setTimeout(() => setSaved(false), 3000);
  };

  if (!hasConsented) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CookieIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Cookie Preferences</Typography>
          </Box>
          <Alert severity="info" icon={<InfoIcon />}>
            You haven't set your cookie preferences yet. Visit the homepage to see the cookie
            consent banner and make your choices.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CookieIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Cookie Preferences</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage your cookie consent choices. You can change these settings at any time.
        </Typography>

        {/* Success Message */}
        {saved && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Your cookie preferences have been updated!
          </Alert>
        )}

        {/* Current Settings Info */}
        <Box
          sx={{
            bgcolor: alpha(theme.palette.info.main, 0.1),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            borderRadius: 1,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>Last updated:</strong> {preferences.timestamp.toLocaleDateString()}{' '}
            {preferences.timestamp.toLocaleTimeString()}
            <br />
            <strong>Policy version:</strong> {preferences.version}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Cookie Categories */}
        <FormGroup>
          {/* Essential - Always On */}
          <FormControlLabel
            control={<Switch checked disabled />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Essential Cookies (Required)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Necessary for authentication, security, and core functionality. These cannot be
                  disabled.
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start', ml: 0 }}
          />

          {/* Preferences */}
          <FormControlLabel
            control={
              <Switch
                checked={localPrefs.preferences}
                onChange={() => handleToggle('preferences')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Preference Cookies
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Remember your settings like theme, language, and timezone for a better
                  experience.
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start', ml: 0 }}
          />

          {/* Analytics */}
          <FormControlLabel
            control={
              <Switch
                checked={localPrefs.analytics}
                onChange={() => handleToggle('analytics')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Analytics Cookies
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Help us understand how you use our service through Google Analytics. This data
                  is anonymized.
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start', ml: 0 }}
          />

          {/* Marketing */}
          <FormControlLabel
            control={
              <Switch
                checked={localPrefs.marketing}
                onChange={() => handleToggle('marketing')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Marketing Cookies
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Used to show you personalized content and ads. We respect your privacy.
                </Typography>
              </Box>
            }
            sx={{ mb: 3, alignItems: 'flex-start', ml: 0 }}
          />
        </FormGroup>

        <Divider sx={{ mb: 3 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
          <Button variant="outlined" color="primary" onClick={handleAcceptAll}>
            Accept All
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleEssentialOnly}>
            Essential Only
          </Button>
        </Box>

        {hasChanges && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have unsaved changes. Click "Save Changes" to apply them.
          </Alert>
        )}

        {/* Footer Links */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Learn more about our{' '}
            <MuiLink component={Link} to="/legal/cookies" underline="hover">
              Cookie Policy
            </MuiLink>{' '}
            and{' '}
            <MuiLink component={Link} to="/legal/privacy" underline="hover">
              Privacy Policy
            </MuiLink>
            .
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CookiePreferencesSettings;
